import { PureComponent, RefObject, useEffect, useRef } from 'react';

import { sortBy } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { useRouterActions } from 'react-resource-router';

import EmptyState from '@atlaskit/empty-state';
import { ErrorMessage, Field } from '@atlaskit/form';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import InlineDialog from '@atlaskit/inline-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Select, { OptionsType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
  BuildStatus as Builds,
  BuildStatusProps,
} from '@atlassian/bitkit-builds';

import Loading from 'src/components/loading';
import { WorkspaceBase } from 'src/sections/account/user-workspaces-resource';
import { getModalMenuPortalStyles } from 'src/styles/select';
import { urls } from 'src/urls/branch';
import { publishUiEvent } from 'src/utils/analytics/publish';

import {
  BRANCH_TYPE_OTHER,
  CREATE_BRANCH_ERROR_TYPE,
  LOADING_STATE,
  REF_TYPE,
  CREATE_FROM,
} from '../constants';
import SelectBranch from '../containers/select-branch';
import {
  BranchType,
  BranchTypeSelector,
  CreateBranchError,
  CreateBranchParams,
  Ref,
  RefSelector,
  RepositorySelector,
  SelectOption,
  WorkflowBranches,
} from '../types';
import { areRefsSame } from '../utils';

import { BranchGraphic } from './branch-graphic';
import { messages } from './create-branch.i18n';
import * as styles from './create-branch.style';
import SelectRepository from './select-repository';
import { WorkspaceSelector } from './workspace-selector';

const boxStyles = xcss({
  borderColor: 'color.border.input',
  borderStyle: 'solid',
  borderWidth: 'border.width.selected',
  borderRightWidth: '0',
  borderTopLeftRadius: 'radius.small',
  borderTopRightRadius: '0',
  borderBottomRightRadius: '0',
  borderBottomLeftRadius: 'radius.small',
  boxSizing: 'unset',
  padding: 'space.100',
  backgroundColor: 'elevation.surface',
  height: '20px',
});

const BRANCHING_MODEL_TYPE_LABEL = {
  bugfix: messages.branchTypeBugfixOptionLabel,
  feature: messages.branchTypeFeatureOptionLabel,
  hotfix: messages.branchTypeHotfixOptionLabel,
  release: messages.branchTypeReleaseOptionLabel,
  other: messages.branchTypeOtherOptionLabel,
};

const BRANCH_TYPE_ORDER = ['bugfix', 'feature', 'hotfix', 'release'];

// these are the create branch error types that will show an error next
//  to the branch name input field and will not show an error flag
export const inlineCreateBranchErrors = [
  CREATE_BRANCH_ERROR_TYPE.BRANCH_ALREADY_EXISTS,
  CREATE_BRANCH_ERROR_TYPE.BRANCH_PERMISSION_VIOLATED,
  CREATE_BRANCH_ERROR_TYPE.INVALID_BRANCH_NAME,
] as const;

export type InlineCreateBranchErrors =
  (typeof inlineCreateBranchErrors)[number];

type CreateBranchProps = {
  branchTypeSelector: BranchTypeSelector;
  intl: IntlShape;
  isCreating: boolean;
  createParams: CreateBranchParams;
  newBranchName: string;
  refSelector: RefSelector;
  error: CreateBranchError | null | undefined;
  workflowBranches: WorkflowBranches;
  onFetchCommitStatuses: () => void;
  onChangeFromBranch: (branch: Ref) => void;
  onChangeNewBranchName: (name: string) => void;
  onChangeBranchType: (branchType: BranchType) => void;
  isBannerOpen: boolean;
  createFrom: CREATE_FROM;
  onChangeRepository: (payload: SelectOption) => void;
  onLoadRepositories: (owner: string, filter?: string) => void;
  repositorySelector: RepositorySelector;
  initialWorkspace: WorkspaceBase | null;
  numberOfWorkspaces: number;
  workspaceOptions?: OptionsType;
};

type CreateBranchState = {
  isBranchTypeSelectorHelpShown: boolean;
  branchNameInputRef: RefObject<HTMLInputElement> | null;
  selectedWorkspaceSlug?: string;
  isLoading: boolean;
};

type BranchNamePrefixProps = {
  prefix: string;
};
const BranchNamePrefix = (props: BranchNamePrefixProps) => {
  const { prefix } = props;
  return (
    <styles.BranchNamePrefix>
      <Tooltip content={prefix} position="bottom">
        <span id="branch-name-prefix">{prefix}</span>
      </Tooltip>
    </styles.BranchNamePrefix>
  );
};

const BranchNameInputField = (
  props: CreateBranchProps &
    CreateBranchState & {
      branchNameInputLabel: string;
      renderInvalidBranchNameMessage: () => JSX.Element | null;
      getSelectedBranchType: () => {
        label: string | JSX.Element;
        value: BranchType;
      };
      branchNameInputRef: RefObject<HTMLInputElement> | null;
      setBranchNameInputRef: (ref: RefObject<HTMLInputElement>) => void;
    }
) => {
  const {
    getSelectedBranchType,
    branchNameInputLabel,
    branchNameInputRef,
    setBranchNameInputRef,
    renderInvalidBranchNameMessage,
  } = props;

  const branchType = getSelectedBranchType();
  const prefix = branchType && branchType.value.prefix;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!branchNameInputRef) {
      setBranchNameInputRef(inputRef);
    }
  }, [branchNameInputRef, setBranchNameInputRef]);

  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const radiusSmallToken = token('radius.small', '3px');

  const inputFieldStyles = xcss({
    borderColor: 'color.border.input',
    borderStyle: 'solid',
    borderWidth: 'border.width.selected',
    borderRadius: prefix
      ? (`0 ${radiusSmallToken} ${radiusSmallToken} 0` as any)
      : radiusSmallToken,
    width: '100%',
    height: '100%',
  });

  const errorMessage = renderInvalidBranchNameMessage();
  return (
    <Field label={branchNameInputLabel} name="branch-name-field">
      {({ fieldProps }) => (
        <>
          <Inline alignBlock="center">
            {prefix && (
              <Box xcss={boxStyles}>
                <BranchNamePrefix prefix={prefix} />
              </Box>
            )}
            <Box xcss={inputFieldStyles}>
              <Textfield
                {...fieldProps}
                name="branchName"
                autoFocus
                onChange={(e: any) =>
                  props.onChangeNewBranchName(e.target.value)
                }
                isDisabled={props.isCreating}
                autoComplete="off"
                isInvalid={!!errorMessage}
                value={props.createParams.name}
                appearance="none"
                ref={inputRef}
              />
            </Box>
          </Inline>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </>
      )}
    </Field>
  );
};

const BuildStatusWithRouter = (props: BuildStatusProps) => {
  const { push } = useRouterActions();
  return <Builds {...props} routerPush={push} />;
};

export class CreateBranch extends PureComponent<
  CreateBranchProps,
  CreateBranchState
> {
  state: CreateBranchState = {
    isBranchTypeSelectorHelpShown: false,
    branchNameInputRef: null,
    selectedWorkspaceSlug: this.props.initialWorkspace?.slug,
    isLoading: false,
  };

  componentDidUpdate(prevProps: CreateBranchProps) {
    const {
      onFetchCommitStatuses,
      error,
      createParams,
      repositorySelector,
      refSelector,
    } = this.props;

    const prevRefHash = ((prevProps.createParams.target as Ref) || {}).hash;
    const currRefHash = ((createParams.target as Ref) || {}).hash;
    if (prevRefHash !== currRefHash && currRefHash) {
      onFetchCommitStatuses();
    }
    const isLoadingFinished =
      prevProps.repositorySelector.loadingState !==
        repositorySelector.loadingState &&
      repositorySelector.loadingState !== LOADING_STATE.LOADING &&
      refSelector.loadingState !== LOADING_STATE.LOADING;

    // Focus on the branch name input field if new state contains related error
    if (
      this.state.branchNameInputRef &&
      !prevProps.error &&
      error &&
      error.type in this.branchNameErrorMessages
    ) {
      this.state.branchNameInputRef?.current?.focus();
    }

    if (this.state.isLoading && isLoadingFinished) {
      this.setState({ isLoading: false });
    }
  }

  setBranchNameInputRef = (ref: RefObject<HTMLInputElement>) =>
    this.setState({ branchNameInputRef: ref });

  handleChangeFromBranch = (ref: Ref) => {
    this.publishEventForChangeFromBranch(ref);

    this.props.onChangeFromBranch(ref);
  };

  publishEventForChangeFromBranch = (newFromBranch: Ref) => {
    const {
      refSelector: { selected, suggestedRef },
      branchTypeSelector: { selected: selectedBranchType, branchTypes },
      workflowBranches: { development, production },
    } = this.props;

    const previous = selected ? selected.ref : null;

    if (!previous || !newFromBranch || previous === newFromBranch) {
      // `previous === newFromBranch` is the case when the dropdown is opened and the existing selected option is chosen.
      // We don't want to send an analytics event for that.
      return;
    }

    const branchType = (ref: Ref): string | null | undefined => {
      if (ref.type === REF_TYPE.TAG) {
        return 'tag';
      }
      // Check if branch name is one of development or production
      if (development && areRefsSame(ref, development)) {
        return 'development';
      } else if (production && areRefsSame(ref, production)) {
        return 'production';
      }

      if (!branchTypes.length) {
        return null;
      }
      // Check if branch name matches a prefix
      const type = branchTypes.find(t => ref.name.indexOf(t.prefix) === 0);
      return type ? type.kind : BRANCH_TYPE_OTHER.kind;
    };

    const isDefault = (ref: Ref): boolean =>
      suggestedRef ? areRefsSame(ref, suggestedRef) : false;

    const attributes = {
      branchType: selectedBranchType ? selectedBranchType.kind : null,
      newIsDefault: isDefault(newFromBranch),
      newType: branchType(newFromBranch),
      previousIsDefault: isDefault(previous),
      previousType: branchType(previous),
    };

    publishUiEvent({
      action: 'changed',
      actionSubject: 'option',
      actionSubjectId: 'fromBranchSelect',
      source: 'createBranchScreen',
      attributes,
    });
  };

  getSelectedBranchType = () => {
    const { selected } = this.props.branchTypeSelector;
    const branchType = selected || BRANCH_TYPE_OTHER;
    return {
      label: this.props.intl.formatMessage(
        BRANCHING_MODEL_TYPE_LABEL[branchType.kind]
      ),
      value: branchType,
    };
  };

  getBranchTypeOptions = () => {
    const { branchTypes } = this.props.branchTypeSelector;
    const options = sortBy(branchTypes, [
      type => BRANCH_TYPE_ORDER.indexOf(type.kind),
    ]).map(type => ({
      label: this.props.intl.formatMessage(
        BRANCHING_MODEL_TYPE_LABEL[type.kind]
      ),
      value: type,
    }));
    if (options.length > 0) {
      options.push({
        label: this.props.intl.formatMessage(
          BRANCHING_MODEL_TYPE_LABEL[BRANCH_TYPE_OTHER.kind]
        ),
        value: BRANCH_TYPE_OTHER,
      });
    }
    return options;
  };

  setHelpShown = (showHelp: boolean) =>
    this.setState({ isBranchTypeSelectorHelpShown: showHelp });

  handleWorkspaceChange = (slug: string) => {
    this.setState({
      selectedWorkspaceSlug: slug,
      isLoading: true,
    });
  };
  renderBranchTypeSelectorLabel = (htmlFor: string) => {
    const { intl } = this.props;
    const { isBranchTypeSelectorHelpShown: helpShown } = this.state;

    const tooltipContent = (
      <styles.BranchTypeSelectorTooltipContent>
        <p>
          <FormattedMessage {...messages.branchTypeTooltipText} />
        </p>
        <p>
          <a
            href={urls.external.docs()}
            target="_blank"
            onClick={() => this.setHelpShown(false)}
          >
            <FormattedMessage {...messages.branchTypeTooltipLearnMoreLink} />
          </a>
        </p>
      </styles.BranchTypeSelectorTooltipContent>
    );

    return (
      <>
        <styles.FormLabel id="branch-type-label" htmlFor={htmlFor}>
          {intl.formatMessage(messages.branchTypeLabel)}
        </styles.FormLabel>
        <styles.BranchTypeSelectorIconWrapper>
          <InlineDialog
            content={tooltipContent}
            isOpen={helpShown}
            onClose={() => this.setHelpShown(false)}
            placement="right-start"
          >
            <styles.CursorPointer
              type="button"
              onClick={() => this.setHelpShown(!helpShown)}
            >
              <StatusInformationIcon size="small" label="" />
            </styles.CursorPointer>
          </InlineDialog>
        </styles.BranchTypeSelectorIconWrapper>
      </>
    );
  };

  hasBuildStatuses = () =>
    this.props.refSelector.selectedCommitStatuses &&
    this.props.refSelector.selectedCommitStatuses.length > 0;

  renderApiErrorMessage = () => {
    const { error } = this.props;
    return <span>{error && error.message}</span>;
  };

  branchNameErrorMessages: { [K in InlineCreateBranchErrors]: () => void } = {
    [CREATE_BRANCH_ERROR_TYPE.BRANCH_ALREADY_EXISTS]: () => {
      return (
        <FormattedMessage {...messages.errorDescriptionBranchAlreadyExists} />
      );
    },
    // For these errors we simply show the message returned in API response
    [CREATE_BRANCH_ERROR_TYPE.BRANCH_PERMISSION_VIOLATED]: () =>
      this.renderApiErrorMessage(),
    [CREATE_BRANCH_ERROR_TYPE.INVALID_BRANCH_NAME]: () =>
      this.renderApiErrorMessage(),
  };

  renderInvalidBranchNameMessage = () => {
    const { error } = this.props;
    if (error) {
      const type = Object.keys(this.branchNameErrorMessages).find(
        t => t === error.type
      );
      if (type) {
        return (
          <styles.BranchNameError>
            {(this.branchNameErrorMessages as any)[type]()}
          </styles.BranchNameError>
        );
      }
    }

    return null;
  };

  renderRepositorySelector = () => {
    return (this.props.createFrom === CREATE_FROM.GLOBAL_PAGE ||
      this.props.createFrom === CREATE_FROM.GLOBAL_DIALOG) &&
      this.state.selectedWorkspaceSlug ? (
      <SelectRepository
        repositorySelector={this.props.repositorySelector}
        intl={this.props.intl}
        isCreating={this.props.isCreating}
        onChangeRepository={this.props.onChangeRepository}
        onLoadRepositories={this.props.onLoadRepositories}
        workspaceSlug={this.state.selectedWorkspaceSlug}
      />
    ) : null;
  };

  renderWorkspaceSelector = () => {
    const { numberOfWorkspaces, workspaceOptions, onLoadRepositories } =
      this.props;

    if (
      this.props.createFrom !== CREATE_FROM.GLOBAL_PAGE ||
      numberOfWorkspaces === 1 ||
      !workspaceOptions ||
      !this.state.selectedWorkspaceSlug
    ) {
      return null;
    }

    return (
      <WorkspaceSelector
        onLoadRepositories={onLoadRepositories}
        onWorkspaceChange={this.handleWorkspaceChange}
        workspaceSlug={this.state.selectedWorkspaceSlug}
        defaultOptions={workspaceOptions}
      />
    );
  };

  render() {
    const {
      intl,
      isCreating,
      refSelector,
      createParams,
      onChangeBranchType,
      branchTypeSelector,
      isBannerOpen,
      numberOfWorkspaces,
    } = this.props;

    const isBranchLoading =
      branchTypeSelector.loadingState === LOADING_STATE.LOADING ||
      refSelector.loadingState === LOADING_STATE.LOADING;

    const targetName = (createParams.target && createParams.target.name) || '';
    const branchTypeId = 'select-branch-type';

    const selectStyles = getModalMenuPortalStyles(isBannerOpen);

    return (
      <>
        {/* TODO: COREX-12354 - Show empty state when user has no accessible workspaces.
        This condition should be refined to check defaultWorkspace once we migrate to the workspace
        resource used by navigation. */}
        {(this.props.createFrom === CREATE_FROM.GLOBAL_PAGE ||
          this.props.createFrom === CREATE_FROM.GLOBAL_DIALOG) &&
        numberOfWorkspaces === 0 ? (
          <EmptyState
            header={intl.formatMessage(messages.emptyStateTitle)}
            description={intl.formatMessage(messages.emptyStateDescription)}
            width="wide"
          />
        ) : (
          <div data-qa={!isBranchLoading ? 'create-branch-loaded' : ''}>
            {this.renderWorkspaceSelector()}
            {this.state.isLoading &&
            this.props.createFrom === CREATE_FROM.GLOBAL_PAGE ? (
              <Box paddingBlockStart="space.150">
                <Loading size="medium" testId="workspace-change-loading" />
              </Box>
            ) : (
              <>
                {this.renderRepositorySelector()}
                {branchTypeSelector.isVisible && (
                  <styles.FieldWrapper>
                    {this.renderBranchTypeSelectorLabel(branchTypeId)}
                    <Select
                      id={branchTypeId}
                      isDisabled={isCreating || isBranchLoading}
                      isLoading={isBranchLoading}
                      options={this.getBranchTypeOptions()}
                      value={this.getSelectedBranchType()}
                      // @ts-ignore TODO: fix noImplicitAny error here
                      onChange={option => onChangeBranchType(option.value)}
                      onClickPreventDefault={false}
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      aria-labelledby="branch-type-label"
                    />
                  </styles.FieldWrapper>
                )}

                <styles.FieldWrapper id="select-branch">
                  <styles.FormLabel
                    id="select-branch-label"
                    htmlFor="select-branch-input"
                  >
                    {intl.formatMessage(messages.fromBranchLabel)}
                  </styles.FormLabel>
                  <styles.FieldRefSelector>
                    <styles.FieldRefSelectorDropdown>
                      <SelectBranch
                        intl={intl}
                        refSelector={refSelector}
                        isCreating={isCreating}
                        isLoading={isBranchLoading}
                        onChangeFromBranch={this.handleChangeFromBranch}
                        selectStyles={selectStyles}
                      />
                    </styles.FieldRefSelectorDropdown>
                    {this.hasBuildStatuses() && (
                      <styles.FieldRefSelectorBuildStatus>
                        <BuildStatusWithRouter
                          builds={refSelector.selectedCommitStatuses}
                        />
                      </styles.FieldRefSelectorBuildStatus>
                    )}
                  </styles.FieldRefSelector>
                </styles.FieldWrapper>
                <BranchNameInputField
                  {...this.props}
                  {...this.state}
                  branchNameInputLabel={intl.formatMessage(
                    messages.branchNameLabel
                  )}
                  renderInvalidBranchNameMessage={
                    this.renderInvalidBranchNameMessage
                  }
                  getSelectedBranchType={this.getSelectedBranchType}
                  setBranchNameInputRef={this.setBranchNameInputRef}
                />
                <BranchGraphic
                  targetBranchName={targetName}
                  newBranchName={this.props.newBranchName}
                />
              </>
            )}
          </div>
        )}
      </>
    );
  }
}
